// traceRouteUtils.js
import { MapUtils } from '../uitls/mapUtils'
import { getCurrentPositionWithFallback, getCachedLocation } from '../uitls/helpers'

/**
 * Résout la position du rider sans déclencher un appel GPS brut.
 * Ordre de priorité :
 *   1. Marker isUserPosition déjà présent dans markersArray (mis à jour par watchUserPosition en temps réel)
 *   2. Cache persistant localStorage (position récente < 15 min)
 *   3. Fallback GPS avec preferCache=true (retourne le cache et rafraîchit en arrière-plan)
 */
async function resolveUserPos(markersArray) {
  // 1. Position déjà trackée en temps réel dans le tableau de markers
  const liveMarker = markersArray?.find(m => m.isUserPosition)
  if (liveMarker?.position?.lat && liveMarker?.position?.lng) {
    return liveMarker.position
  }

  // 2. Cache persistant récent (< 15 min)
  const cached = getCachedLocation(15 * 60 * 1000)
  if (cached?.lat && cached?.lng) {
    return cached
  }

  // 3. Dernier recours : preferCache=true lance le GPS mais retourne immédiatement le cache si disponible
  return getCurrentPositionWithFallback({ preferCache: true, maxCacheAge: 60 * 60 * 1000 })
}
import { animateRoute } from './routeAnimations.js'

export async function traceRouteForSteps({
  id,
  steps,
  map,
  markersArray,
  routesObj,
  abortController,
}) {
  // abortController?.abort()
  // const controller = new AbortController()
  // // Nettoyage
  // routesObj[id] && delete routesObj[id]
  // for (let i = markersArray.length - 1; i >= 0; i--) {
  //   if (markersArray[i].routeId === id) markersArray.splice(i, 1)
  // }
  // // Marker départ
  // markersArray.push({ position: steps[0].start, isUserPosition: false, routeId: id, options: { title: 'Départ' } })
  // // Construction route principale
  // const routeMain = []
  // for (let i = 0; i < steps.length; i++) {
  //   const s = steps[i]
  //   const sub = await MapUtils.traceRoute(s.start, s.end, [], true)
  //   markersArray.push({ position: s.start, isUserPosition: false, routeId: id, options: { title: `Étape ${i + 1}` } })
  //   sub[0].steps.forEach(st => routeMain.push(...st.detailSteps))
  // }
  // // Marker arrivée
  // markersArray.push({ position: steps[steps.length - 1].end, isUserPosition: false, routeId: id, options: { title: 'Arrivée' } })
  // animateRoute({ routesObj, map, routeId: id, routePoints: routeMain, abortSignal: controller.signal, options })
  // return controller
}

export async function traceRouteFromUser({
  id,
  steps,
  map,
  markersArray,
  routesObj,
  options,
}) {
  let controller = new AbortController()
  routesObj[id] && delete routesObj[id]
  for (let i = markersArray.length - 1; i >= 0; i--)
    if (markersArray[i].routeId === id) markersArray.splice(i, 1)

  let userPos
  try {
    userPos = await resolveUserPos(markersArray)
  } catch (error) {
    console.warn(
      "Impossible d'obtenir la position utilisateur pour le tracé de route:",
      error instanceof Error ? error.message : String(error),
    )
    return null
  }
  if (!userPos) return null

  // Route vers le premier step
  const routeToStart = await MapUtils.traceRoute(
    userPos,
    steps[0].start,
    [],
    true,
  )
  markersArray.push({
    position: userPos,
    isUserPosition: true,
    options: { title: 'Départ' },
  })
  markersArray.push({
    position: steps[0].start,
    isUserPosition: false,
    routeId: id,
    options: { title: 'Étape 1' },
  })

  const routeLegs = []

  // Premier leg : utilisateur vers premier point
  const firstLeg = []
  routeToStart[0].steps.forEach(st => firstLeg.push(...st.detailSteps))
  routeLegs.push(firstLeg)

  // Traiter tous les steps comme des legs séparés
  for (let i = 0; i < steps.length; i++) {
    const sub = await MapUtils.traceRoute(
      steps[i].start,
      steps[i].end,
      [],
      true,
    )
    markersArray.push({
      position: steps[i].start,
      isUserPosition: false,
      routeId: id,
      options: { title: `Étape ${i + 1}` },
    })

    const legPoints = []
    sub[0].steps.forEach(st => legPoints.push(...st.detailSteps))
    routeLegs.push(legPoints)
  }
  markersArray.push({
    position: steps[steps.length - 1].end,
    isUserPosition: false,
    routeId: id,
    options: { title: 'Arrivée' },
  })

  animateRoute({
    routesObj,
    map,
    routeId: id,
    routeLegs: routeLegs, // Nouveau paramètre pour les legs séparés
    abortSignal: controller.signal,
    options,
  })
  return controller
}

export async function traceRouteWithPoints({
  id,
  start,
  end,
  checkpoints = [],
  map,
  markersArray,
  routesObj,
  options,
}) {
  let controller = new AbortController()

  // Nettoyer les anciens marqueurs et routes pour cet ID
  if (routesObj[id]) delete routesObj[id]
  for (let i = markersArray.length - 1; i >= 0; i--) {
    if (markersArray[i].routeId === id) markersArray.splice(i, 1)
  }

  // Filtrer les checkpoints pour éviter les valeurs nulles ou indéfinies
  const validCheckpoints = (checkpoints || []).filter(cp => cp && typeof cp === 'object' && cp.lat !== undefined && cp.lng !== undefined)

  // Ajouter les marqueurs
  if (start && start.lat !== undefined && start.lng !== undefined) {
    markersArray.push({
      position: start,
      isUserPosition: false,
      routeId: id,
      content: start.content || '',
      iconType: start.iconType || 'pickup',
      options: { title: start.content || '' },
    })
  }

  validCheckpoints.forEach((cp, i) => {
    markersArray.push({
      position: cp,
      isUserPosition: false,
      routeId: id,
      content: cp.content || '',
      iconType: cp.iconType || 'checkpoint',
      checkpointNumber: i + 1,
      options: { title: cp.content || '' },
    })
  })

  if (end && end.lat !== undefined && end.lng !== undefined) {
    markersArray.push({
      position: end,
      isUserPosition: false,
      routeId: id,
      content: end.content || '',
      iconType: end.iconType || 'destination',
      options: { title: end.content || 'Destination' },
    })
  }

  try {
    // Vérifier si on a au moins un départ et une arrivée valides
    if (!start || start.lat === undefined || !end || end.lat === undefined) {
      console.warn('[traceRouteWithPoints] Points de départ ou d\'arrivée invalides', { start, end })
      return controller
    }

    // Tracer la route avec les checkpoints valides
    const route = await MapUtils.traceRoute(start, end, validCheckpoints, true)

    // Garder les legs séparés pour avoir des couleurs différentes par segment
    const routeLegs = route.map(leg => {
      const legPoints = []
      leg.steps.forEach(st => legPoints.push(...st.detailSteps))
      return legPoints
    })

    // Animer la route avec les legs séparés
    animateRoute({
      routesObj,
      map,
      routeId: id,
      routeLegs: routeLegs, // Nouveau paramètre pour les legs séparés
      abortSignal: controller.signal,
      options,
    })

    return controller
  } catch (error) {
    controller.abort()
    throw error
  }
}

export async function traceRouteFromUserWithPoints({
  id,
  start,
  end,
  checkpoints = [],
  map,
  markersArray,
  routesObj,
  options,
}) {
  let controller = new AbortController()
  routesObj[id] && delete routesObj[id]
  for (let i = markersArray.length - 1; i >= 0; i--)
    if (markersArray[i].routeId === id) markersArray.splice(i, 1)

  let userPos
  try {
    userPos = await resolveUserPos(markersArray)
  } catch (error) {
    console.warn(
      "Impossible d'obtenir la position utilisateur pour le tracé de route:",
      error instanceof Error ? error.message : String(error),
    )
    return null
  }
  if (!userPos) return null

  // Filtrer les checkpoints pour éviter les valeurs nulles ou indéfinies
  const validCheckpoints = (checkpoints || []).filter(cp => cp && typeof cp === 'object' && cp.lat !== undefined && cp.lng !== undefined)

  // Marqueurs
  markersArray.push({
    position: userPos,
    isUserPosition: true,
    routeId: id,
    options: { title: 'Votre position' },
  })

  if (start && start.lat !== undefined && start.lng !== undefined) {
    markersArray.push({
      position: start,
      isUserPosition: false,
      routeId: id,
      content: start.content || 'Restaurant',
      iconType: 'pickup',
      options: { title: start.content || 'Restaurant' },
    })
  }

  validCheckpoints.forEach((cp, i) => {
    markersArray.push({
      position: cp,
      isUserPosition: false,
      routeId: id,
      content: cp.content || `Point de passage ${i + 1}`,
      iconType: 'checkpoint',
      checkpointNumber: i + 1,
      options: { title: cp.content || `Point de passage ${i + 1}` },
    })
  })

  if (end && end.lat !== undefined && end.lng !== undefined) {
    markersArray.push({
      position: end,
      isUserPosition: false,
      routeId: id,
      content: end.content || 'Destination',
      iconType: 'destination',
      options: { title: end.content || 'Destination' },
    })
  }

  try {
    // Vérifier si on a au moins un départ et une arrivée valides
    if (!start || start.lat === undefined || !end || end.lat === undefined) {
      console.warn('[traceRouteFromUserWithPoints] Points de départ ou d\'arrivée invalides', { start, end })
      return controller
    }

    // Route utilisateur vers départ
    const routeToStart = await MapUtils.traceRoute(userPos, start, [], true)
    const userLeg = []
    if (routeToStart[0])
      routeToStart[0].steps.forEach(st => userLeg.push(...st.detailSteps))

    // Route principale avec checkpoints valides
    const route = await MapUtils.traceRoute(start, end, validCheckpoints, true)
    const mainLegs = route.map(leg => {
      const legPoints = []
      leg.steps.forEach(st => legPoints.push(...st.detailSteps))
      return legPoints
    })

    // Combiner tous les legs dans l'ordre
    const allLegs = [userLeg, ...mainLegs]

    // Animer la route avec les legs séparés
    animateRoute({
      routesObj,
      map,
      routeId: id,
      routeLegs: allLegs, // Nouveau paramètre pour les legs séparés
      abortSignal: controller.signal,
      options,
    })

    return controller
  } catch (error) {
    controller.abort()
    throw error
  }
}
